import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import keycloakInstance from "@/initKeycloak";
import { diagnosticEventLog } from "@/shared/services/diagnostic";

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

// Mutex to prevent concurrent refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

/**
 * Axios response interceptor that catches authentication failures (401, plus
 * 403 with `{detail: "Not authenticated"}` since some backends return 403 for
 * expired tokens), attempts a Keycloak token refresh, and retries the original
 * request with the new token.
 *
 * Key design decisions:
 * - No logout — if refresh fails, the original error propagates. The periodic
 *   refresh in useKeycloakTokenHandler or onAuthRefreshError handles logout.
 * - Mutex — concurrent failures coalesce into a single refresh call.
 * - Single retry — _retried flag prevents infinite loops.
 */
export function attachRetryOn401(client: AxiosInstance): void {
  client.interceptors.response.use(undefined, async (error: AxiosError) => {
    const config = error.config as RetryableConfig | undefined;

    const status = error.response?.status;
    const data = error.response?.data as { detail?: string } | undefined;
    // Treat 403 "Not authenticated" the same as 401 — some backends return 403
    // for expired tokens. 403 with any other body is a real authorization
    // failure (insufficient permissions) and shouldn't trigger a token refresh.
    const isAuthFailure =
      status === 401 ||
      (status === 403 && data?.detail === "Not authenticated");

    if (!isAuthFailure || !config || config._retried) {
      return Promise.reject(error);
    }

    if (!keycloakInstance.authenticated) {
      return Promise.reject(error);
    }

    // Coalesce concurrent refreshes — only one in-flight at a time
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = keycloakInstance
        .updateToken(5)
        .then((refreshed) => {
          diagnosticEventLog.record("auth:interceptor", "info", "token-refreshed", {
            refreshed,
          });
          return true;
        })
        .catch(() => {
          diagnosticEventLog.record("auth:interceptor", "warn", "token-refresh-failed");
          return false;
        })
        .finally(() => {
          isRefreshing = false;
          refreshPromise = null;
        });
    }

    const refreshed = await refreshPromise;
    if (!refreshed) {
      return Promise.reject(error);
    }

    // Retry with fresh token
    config._retried = true;
    config.headers.Authorization = `Bearer ${keycloakInstance.token}`;
    return client(config);
  });
}
